import React, { useState } from "react";
import { filter } from "ramda";
import { Box, SolidDivider } from "@thecb/components";
import { noop } from "@/util/common";
import SearchBar from "@/components/search-bar";

import CriteriaForm from "./CriteriaForm";
import FiltersForm, { PAYMENT_METHOD_FILTER } from "./FiltersForm";
import PaymentsBetaBanner from "./PaymentsBetaBanner";

import Pill from "@/components/generic/Pill";

import TransactionSearchResults from "@/components/transactionSearchResults";
import {
  BetaAnnouncementRow,
  SearchPageContainer,
  SearchPageContent,
} from "./TransactionSearchPage.styled";

// The transaction search that predates natural-language search, which lives
// on the Payments - Beta tab. Remove this file once the beta replaces the
// classic tab.

const descriptionFromKey = (key) =>
  key
    .split(/(?=[A-Z])/)
    .join(" ")
    .split("")
    .map((letter, index) =>
      index === 0 ? letter.toUpperCase() : letter.toLowerCase()
    )
    .join("");

const createTransactionParameterPills = (
  criteria,
  filters,
  criteriaActions,
  filterActions,
  setSearchBarOpen
) => {
  const filteredCriteria = filter((value) => value.rawValue !== "", criteria);
  const filteredFilters = filter((value) => value.rawValue !== "", filters);
  return [
    ...Object.keys(filteredCriteria).map((key) => (
      <Box padding="4px" key={key} maxWidth="fit-content">
        <Pill
          onClickBody={setSearchBarOpen}
          valueDescription={
            key === PAYMENT_METHOD_FILTER
              ? "Payment method type"
              : descriptionFromKey(key)
          }
          value={filteredCriteria[key].rawValue}
          onDelete={() => criteriaActions.fields[key].set("")}
          onBlur={noop}
          onChange={(e) => criteriaActions.fields[key].set(e.target.value)}
          editable
          isError={filteredCriteria[key].dirty && filteredCriteria[key].hasErrors}
        />
      </Box>
    )),
    ...Object.keys(filteredFilters).map((key) => (
      <Box padding="4px" key={key} maxWidth="fit-content">
        <Pill
          onClickBody={setSearchBarOpen}
          valueDescription={
            key === PAYMENT_METHOD_FILTER
              ? "Payment method type"
              : descriptionFromKey(key)
          }
          value={
            filteredFilters[key].rawValue === "wire_payment"
              ? "wire transfer"
              : filteredFilters[key].rawValue.replace(/_/g, " ")
          }
          onDelete={() => filterActions.fields[key].set("")}
          onBlur={noop}
          onChange={noop}
          editable={false}
          isError={filteredFilters[key].dirty && filteredFilters[key].hasErrors}
        />
      </Box>
    )),
  ];
};

const LegacyTransactionSearchPage = ({
  actions,
  forms,
  requestSearch,
  selectedDateFilter,
  selectDateFilter,
  searchRequestPending,
  ...props
}) => {
  const [searchBarOpen, setSearchBarOpen] = useState(false);

  const criteriaForm = forms.criteriaForm.fields;
  const criteriaActions = actions.forms.criteriaForm.actions;
  const filtersForm = forms.filtersForm.fields;
  const filtersActions = actions.forms.filtersForm.actions;

  const pills = createTransactionParameterPills(
    criteriaForm,
    filtersForm,
    criteriaActions,
    filtersActions,
    setSearchBarOpen
  );

  const submitSearch = () => {
    const validCriteria = !Object.keys(criteriaForm).some(
      (field) => criteriaForm[field].hasErrors
    );

    if (validCriteria) {
      requestSearch();
    }
  };

  return (
    <SearchPageContainer>
      <BetaAnnouncementRow>
        <PaymentsBetaBanner />
      </BetaAnnouncementRow>
      <SearchPageContent
        $align="flex-start"
        data-testid="legacy-search-page-content"
      >
        <SearchBar
          isOpen={searchBarOpen}
          onClick={searchBarOpen ? noop : () => setSearchBarOpen(true)}
          onClickAway={() => {
            setSearchBarOpen(false);
          }}
          onSearch={searchBarOpen ? () => setSearchBarOpen(false) : noop}
          searchAction={submitSearch}
          defaultText="Search payments"
          bodyOpenText="Build your search below"
          searchBarContent={pills}
          clearSearchBarContent={(evt) => {
            evt.stopPropagation();
            criteriaActions.form.clear();
            filtersActions.form.clear();
          }}
          selectDateFilter={selectDateFilter}
          selectedDateFilter={selectedDateFilter}
          searchRequestPending={searchRequestPending}
        >
          <CriteriaForm
            actions={criteriaActions}
            fields={criteriaForm}
            descriptionFromKey={descriptionFromKey}
            {...props}
          />
          <SolidDivider />
          <FiltersForm
            actions={filtersActions}
            fields={filtersForm}
            descriptionFromKey={descriptionFromKey}
            {...props}
          />
        </SearchBar>
        <TransactionSearchResults />
      </SearchPageContent>
    </SearchPageContainer>
  );
};

export default LegacyTransactionSearchPage;
